export const currentUploadBatchStorageKey = (organizationId, userId) =>
    `current-upload-batches:${organizationId}:${userId}`;

export const batchIsTerminal = batch => batch.deleted || batch.preparationError ||
    (batch.prepared && batch.completed + batch.failed + (batch.removed || 0) >= batch.total);

// Failed files can be retried in this same batch. Keep reconciling them while
// this current session is displayed, without loading historical uploads.
export const batchNeedsRefresh = batch => !batch.deleted && !batch.preparationError &&
    (!batchIsTerminal(batch) || (!batch.canceled && batch.failed > 0));

export const batchTransferredBytes = batch => Math.min(batch.totalBytes,
    Math.max(batch.serverTransferredBytes || 0,
        Object.values(batch.files || {}).reduce((total, file) => total + (file.transferred || 0), 0)));

export const batchIsDeleted = batch => batch.deleted || (batch.total > 0 && batch.deletedCount === batch.total);

export const batchIsTransferring = batch => !batchIsTerminal(batch) && !batch.canceled &&
    (!batch.prepared || (batch.pending != null && batch.uploading != null
        ? batch.pending + batch.uploading > 0 : batchTransferredBytes(batch) < batch.totalBytes));

export const currentUploadBatchesReducer = (batches = {}, action) => {
    if (action.type === 'restoreCurrentUploadBatches') return action.batches || {};
    if (action.type === 'currentUploadBatchDeleted') {
        const batch = batches[action.id];
        if (!batch) return batches;
        return {...batches, [action.id]: {...batch, deleted: true, prepared: true,
            completed: 0, failed: 0, removed: batch.total, deletedCount: batch.total, trashed: 0}};
    }
    if (action.type === 'forgetCurrentUploadBatch') {
        const {[action.id]: removed, ...remaining} = batches;
        return remaining;
    }
    if (action.type === 'beginCurrentUploadBatch') {
        const {id, uploadGuid, files} = action;
        const current = Object.values(batches).some(batch => !batchIsTerminal(batch)) ? batches : {};
        return {...current, [id]: {
            id, uploadGuid, total: files.length,
            totalBytes: files.reduce((total, file) => total + file.file_size, 0),
            files: Object.fromEntries(files.map(file => [file.upload_guid, {size: file.file_size, transferred: 0}])),
            completed: 0, failed: 0, prepared: false,
        }};
    }
    if (action.type === 'currentUploadBatchStatus') {
        const batch = batches[action.id];
        if (!batch) return batches;
        const progress = action.status.progress;
        if (!progress) return batches;
        const prepared = action.status.status === 'ready';
        return {...batches, [action.id]: {
            ...batch,
            prepared: batch.prepared || prepared,
            preparationError: action.status.status === 'error',
            total: prepared ? (batch.prepared ? Math.max(batch.total, progress.total) : progress.total) : batch.total,
            totalBytes: prepared ? (batch.prepared ? Math.max(batch.totalBytes, progress.total_bytes) : progress.total_bytes) : batch.totalBytes,
            serverTransferredBytes: Math.max(batch.serverTransferredBytes || 0, progress.transferred_bytes || 0),
            // These are mutually exclusive current states. A processed version
            // can be removed, so retaining a completion high-water mark would
            // count that file twice and stop polling other processing files.
            completed: progress.completed || 0,
            failed: progress.failed || 0,
            removed: progress.removed || 0,
            trashed: progress.trashed || 0,
            deletedCount: progress.deleted || 0,
            pending: progress.pending,
            uploading: progress.uploading,
            uploadState: progress.upload_state,
            canceled: batch.canceled || progress.batch_canceled === true,
            name: progress.name,
        }};
    }
    if (action.type === 'uploadProgress' || action.type === 'currentUploadTransferFinished') {
        const guid = action.asset?.upload_guid;
        return Object.fromEntries(Object.entries(batches).map(([id, batch]) => {
            const file = batch.files?.[guid];
            if (!file) return [id, batch];
            const transferred = action.type === 'currentUploadTransferFinished' ? file.size :
                Math.min(file.size, Math.max(file.transferred, action.stats?.totalUploaded || 0));
            return [id, {...batch, files: {...batch.files, [guid]: {...file, transferred}}}];
        }));
    }
    if (action.type === 'clearUploadingAssets') {
        return Object.fromEntries(Object.entries(batches).map(([id, batch]) => [id,
            batch.uploadGuid === action.uploadGuid ? {...batch, canceled: true} : batch,
        ]));
    }
    return batches;
};
